/**
 * YoutubeTranscriptFallbackNotice — the manual-copy-paste fallback shown next
 * to the reference-video YouTube URL field when the coach's account cannot
 * use the one-click "Import transcript" button (`user.can_import_youtube`
 * false).
 *
 * FE-10 fix: the fallback copy used to always tell the coach to paste the
 * transcript "into the Paste Notes tab in the Coach Instruction panel
 * above" — but that tab only exists for a paid coach. `TeachingVideoUploader`
 * replaces the whole tabbed panel with `PaidUpsellCard` for a non-paid
 * coach (see `@/utils/coachAccess.isPaidCoach`, the SAME predicate used
 * here so the two surfaces cannot drift), so a non-paid coach was being told
 * to use a UI element they cannot see.
 *
 * Extracted into its own component (rather than left inline in
 * CreateTemplate.tsx) so this paid/non-paid copy branch has a small,
 * independently testable surface — CreateTemplate itself pulls in the full
 * template-wizard state and is not practical to mount in a focused unit
 * test.
 */

export interface YoutubeTranscriptFallbackNoticeProps {
  /** Same predicate as `TeachingVideoUploader`'s gate — see `@/utils/coachAccess.isPaidCoach`. */
  paidCoach: boolean;
}

export default function YoutubeTranscriptFallbackNotice({
  paidCoach,
}: YoutubeTranscriptFallbackNoticeProps) {
  if (paidCoach) {
    return (
      <p className="text-xs text-text-muted leading-relaxed">
        To use this video&apos;s transcript: open the video on YouTube, click{' '}
        <strong className="text-text-secondary font-medium">&#8230; more</strong>{' '}
        below the player, then{' '}
        <strong className="text-text-secondary font-medium">Show transcript</strong>.
        Copy all the text and paste it into the{' '}
        <strong className="text-text-secondary font-medium">Paste Notes</strong> tab
        in the Coach Instruction panel above.
      </p>
    );
  }

  // Non-paid coach: the Coach Instruction panel above shows an upsell card,
  // not the tabbed uploader — never name a tab this coach cannot see.
  // What: nothing is lost — the video link above is saved either way.
  // Why: turning a transcript into suggested cues/goals is a paid feature.
  // How: upgrade to turn transcripts into suggestions automatically.
  return (
    <p className="text-xs text-text-muted leading-relaxed">
      Your video link above is saved as-is. Turning its transcript into
      suggested cues and goals is a paid coach feature —{' '}
      <a
        href="/pricing"
        className="text-accent hover:underline font-medium"
      >
        upgrade to turn it on
      </a>
      .
    </p>
  );
}
